import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { PencilSquareIcon } from '@heroicons/react/24/outline';
import { intakeApi } from '../utils/jobIntake';
import { downloadJob, displayTitle } from '../utils/jobExport';
import { MY_JOBS } from './JobIntakeFormPage';
import { SectionView } from '../components/JobRecruiterTabs';
import ScreeningQuestionCard, { SkillChips } from '../components/ScreeningQuestionCard';

export const POLL_MS = 5000;
export const linesToList = (text) => String(text || '').split('\n').map((l) => l.trim()).filter(Boolean);
const listToLines = (list) => (Array.isArray(list) ? list.join('\n') : '');

// "Label: value" / "Category: skills" lines ↔ the JD's tag and skills-table rows
const pairsToLines = (rows, a, b) => (rows || []).map((r) => `${r[a]}: ${r[b]}`).join('\n');
const linesToPairs = (text, a, b) => linesToList(text).map((l) => {
  const i = l.indexOf(':');
  return i > 0 ? { [a]: l.slice(0, i).trim(), [b]: l.slice(i + 1).trim() } : { [a]: '', [b]: l };
});
// Text shown in a section editor ↔ the section's value
export function sectionToText(kind, value) {
  if (kind === 'tags') return pairsToLines(value, 'label', 'value');
  if (kind === 'table') return pairsToLines(value, 'category', 'skills');
  if (kind === 'paragraphs') return (value || []).join('\n\n');
  return listToLines(value);
}
export function textToSection(kind, text) {
  if (kind === 'tags') return linesToPairs(text, 'label', 'value');
  if (kind === 'table') return linesToPairs(text, 'category', 'skills');
  if (kind === 'paragraphs') return String(text || '').split(/\n\s*\n/).map((p) => p.trim()).filter(Boolean);
  return linesToList(text);
}

// The coloured section cards of the Requirement Results page, one style per JD section
const SECTION_STYLE = {
  focus_tags: { bar: 'bg-blue-500', badge: 'bg-blue-50 border-blue-200 text-blue-800', icon: '📌', accent: 'border-l-blue-500', bg: 'bg-blue-50' },
  summary: { bar: 'bg-blue-500', badge: 'bg-blue-50 border-blue-200 text-blue-800', icon: '📌', accent: 'border-l-blue-500', bg: 'bg-blue-50' },
  responsibilities: { bar: 'bg-cyan-500', badge: 'bg-cyan-50 border-cyan-200 text-cyan-800', icon: '🎯', accent: 'border-l-cyan-500', bg: 'bg-cyan-50' },
  required: { bar: 'bg-red-500', badge: 'bg-red-50 border-red-200 text-red-800', icon: '⭐', accent: 'border-l-red-500', bg: 'bg-red-50' },
  preferred: { bar: 'bg-purple-500', badge: 'bg-purple-50 border-purple-200 text-purple-800', icon: '✨', accent: 'border-l-purple-500', bg: 'bg-purple-50' },
  skills_table: { bar: 'bg-teal-500', badge: 'bg-teal-50 border-teal-200 text-teal-800', icon: '🛠️', accent: 'border-l-teal-500', bg: 'bg-teal-50' },
  locked: { bar: 'bg-indigo-500', badge: 'bg-indigo-50 border-indigo-200 text-indigo-800', icon: '📍', accent: 'border-l-indigo-500', bg: 'bg-indigo-50' },
};
const DEFAULT_STYLE = { bar: 'bg-gray-400', badge: 'bg-gray-50 border-gray-200 text-gray-700', icon: '📄', accent: 'border-l-gray-400', bg: 'bg-gray-50' };
const EMPTY_Q = { skill: '', question: '', expected_answer: '', red_flags: '', source: 'hm' };
const plainBtn = 'px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 font-medium text-sm transition-colors';

// Responsibilities: one heading + bullets box per group
function GroupsEditor({ groups, onChange }) {
  const set = (i, patch) => onChange(groups.map((g, j) => (j === i ? { ...g, ...patch } : g)));
  return (
    <div className="space-y-3">
      {groups.map((g, i) => (
        <div key={i} className="border border-gray-200 rounded-lg p-2 bg-white">
          <div className="flex gap-2">
            <input aria-label={`Group ${i + 1} heading`} value={g.heading} onChange={(e) => set(i, { heading: e.target.value })}
              className="flex-1 px-2 py-1.5 border border-gray-200 rounded text-sm font-semibold" />
            <button onClick={() => onChange(groups.filter((_, j) => j !== i))} aria-label={`Remove group ${i + 1}`} className="px-2 text-gray-400 hover:text-red-600">✕</button>
          </div>
          <textarea aria-label={`Group ${i + 1} bullets`} rows={4} value={g.text}
            onChange={(e) => set(i, { text: e.target.value })}
            className="mt-2 w-full px-2 py-1.5 border border-gray-200 rounded text-sm" />
        </div>
      ))}
      <button onClick={() => onChange([...groups, { heading: '', text: '' }])} className="text-xs font-bold text-brand-blue">+ Add group</button>
    </div>
  );
}

function DownloadButton({ onDownload, busy }) {
  const [open, setOpen] = useState(false);
  const pick = (format) => { setOpen(false); onDownload(format); };
  return (
    <div className="relative">
      <button onClick={() => setOpen((o) => !o)} disabled={busy} aria-haspopup="menu"
        className="px-6 py-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl hover:shadow-lg font-semibold text-sm transition-all disabled:opacity-50">
        📥 Download
      </button>
      {open && (
        <div role="menu" className="absolute right-0 mt-1 w-60 bg-white border border-gray-200 rounded-xl shadow-lg z-10 py-1">
          <button role="menuitem" onClick={() => pick('pdf')} className="block w-full text-left px-4 py-2 text-sm hover:bg-gray-50">PDF</button>
          <button role="menuitem" onClick={() => pick('docx')} className="block w-full text-left px-4 py-2 text-sm hover:bg-gray-50">Word (DOCX)</button>
          <p className="px-4 pb-1 text-[11px] text-gray-400">Job description, screening questions and a printable scorecard</p>
        </div>
      )}
    </div>
  );
}

// HM review of the generated job — the look of the Requirement Results page: a Job Description tab of collapsible
// section cards (pencil = edit that section) and a Screening Questions tab of question cards. Approve locks a version.
// The recruiter brief is never sent to this page (the API leaves it out).
function JobReviewPage() {
  const { intakeId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const [tab, setTab] = useState('jd');
  const [collapsed, setCollapsed] = useState({});        // section key → true when collapsed (all open at first)
  const [editing, setEditing] = useState(null);          // JD section key
  const [draft, setDraft] = useState(null);              // the section editor's text / groups
  const [qEditing, setQEditing] = useState(null);        // question index, or 'new'
  const [confirmRegen, setConfirmRegen] = useState(false);
  const [changing, setChanging] = useState(false);       // approved version → "Make changes"
  const [showHistory, setShowHistory] = useState(false);
  const [viewing, setViewing] = useState(null);

  const load = useCallback(async () => {
    const res = await intakeApi(`/${intakeId}/review`);
    setData(res);
    return res;
  }, [intakeId]);

  useEffect(() => { load().catch((e) => setError(e.message)); }, [load]);

  useEffect(() => {
    // Keep checking while the documents are on their way (call just ended → transcript → generation)
    if (!data || !['generating', 'call_done'].includes(data.status) || data.current) return undefined;
    const id = setInterval(() => { load().catch(() => {}); }, POLL_MS);
    return () => clearInterval(id);
  }, [data, load]);

  if (error && !data) return <div className="p-10 text-center text-red-600">{error}</div>;
  if (!data) return <div className="p-10 text-center text-gray-500">Loading…</div>;

  const run = async (label, fn) => {
    setBusy(label); setError('');
    let ok = true;
    try { await fn(); } catch (e) { setError(e.message); ok = false; }
    setBusy('');
    return ok;
  };
  const generate = () => run('generate', async () => {
    try { await intakeApi(`/${intakeId}/generate`, { method: 'POST' }); } finally { await load(); }   // always show the latest state
  });
  const back = () => navigate(MY_JOBS.pathname, { state: MY_JOBS.state });

  if (!data.current) {
    return (
      <div className="min-h-screen bg-gray-50 font-inter">
        <header className="bg-white border-b border-gray-200">
          <div className="max-w-7xl mx-auto px-6 py-4">
            <button onClick={back} className="text-xs text-gray-500 hover:text-brand-blue">← My jobs</button>
            <h1 className="text-xl font-black text-gray-900" data-testid="page-title">{displayTitle(data.req_id, data.job_title) || 'New job'}</h1>
          </div>
        </header>
        <main className="max-w-7xl mx-auto px-6 py-8">
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6" data-testid="not-ready">
            {data.status === 'generating' && !data.generation_delayed && <p className="text-gray-700">Preparing your job description, screening questions and recruiter brief… this takes about a minute.</p>}
            {data.status === 'generating' && data.generation_delayed && (
              <p className="text-amber-700" data-testid="generation-delayed">Generation is delayed — we'll keep trying. You can leave this page; the documents will be here when they're ready.</p>
            )}
            {data.status === 'failed' && <p className="text-red-600">{data.generation_error || 'Generation failed.'}</p>}
            {['call_done', 'call_paused'].includes(data.status) && (
              <p className="text-gray-700">{data.status === 'call_done' ? 'Waiting for the call transcript…' : 'The call with Jessica is paused.'} You can generate the documents now.</p>
            )}
            {['call_done', 'call_paused', 'failed'].includes(data.status) && (
              <button onClick={generate} disabled={!!busy} className="mt-4 px-5 py-2.5 rounded-xl bg-brand-blue text-white text-sm font-bold disabled:opacity-50">
                {busy === 'generate' ? 'Starting…' : data.status === 'failed' ? 'Try again' : 'Generate now'}
              </button>
            )}
            {error && <p className="mt-3 text-sm text-red-600">{error}</p>}
          </div>
        </main>
      </div>
    );
  }

  const cur = data.current;
  const jd = cur.jd || {};
  const questions = cur.screening_questions || [];
  const approved = cur.status === 'approved';
  const canEdit = !approved || changing;

  // Every edit is saved straight away (an edit to an approved version starts a new draft version)
  const persist = (label, nextJd, nextQs) => run(label, async () => {
    const res = await intakeApi(`/${intakeId}/review`, { method: 'PUT', body: { jd: nextJd, screening_questions: nextQs } });
    setData(res);
    setChanging(false);
  });
  const approve = () => run('approve', async () => { setData(await intakeApi(`/${intakeId}/approve`, { method: 'POST' })); });
  const regenerate = () => run('regenerate', async () => {
    setData(await intakeApi(`/${intakeId}/questions/regenerate`, { method: 'POST' }));
    setConfirmRegen(false);
    setQEditing(null);
  });

  const startEdit = (s) => {
    setCollapsed((c) => ({ ...c, [s.key]: false }));
    setEditing(s.key);
    setDraft(s.kind === 'groups'
      ? (jd[s.key] || []).map((g) => ({ heading: g.heading, text: listToLines(g.bullets) }))
      : sectionToText(s.kind, jd[s.key]));
  };
  const saveSection = async (s) => {
    const value = s.kind === 'groups'
      ? draft.map((g) => ({ heading: g.heading.trim(), bullets: linesToList(g.text) })).filter((g) => g.bullets.length)
      : textToSection(s.kind, draft);
    if (await persist('section', { ...jd, [s.key]: value }, questions)) setEditing(null);
  };

  const saveQuestion = async (q) => {
    const next = qEditing === 'new' ? [...questions, q] : questions.map((x, i) => (i === qEditing ? q : x));
    if (await persist('question', jd, next)) setQEditing(null);
  };
  const removeQuestion = (i) => persist('question', jd, questions.filter((_, j) => j !== i));
  const move = (i, d) => {
    const next = [...questions];
    [next[i], next[i + d]] = [next[i + d], next[i]];
    persist('question', jd, next);
  };
  const download = (format) => run('download', () => downloadJob(format, { intakeId, reqId: data.req_id, jobTitle: data.job_title,
    versionNo: cur.version_no, jd, questions }));
  const sections = data.sections || [];

  return (
    <div className="min-h-screen bg-gray-50 font-inter">
      <header className="bg-white border-b border-gray-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 py-4 flex flex-wrap items-center justify-between gap-3">
          <div>
            <button onClick={back} className="text-xs text-gray-500 hover:text-brand-blue">← My jobs</button>
            <h1 className="text-xl font-black text-gray-900" data-testid="page-title">{displayTitle(data.req_id, data.job_title) || 'New job'}</h1>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <span data-testid="version-status"
              className={`px-3 py-1 rounded-full text-xs font-bold ${approved ? 'bg-green-50 text-green-700' : 'bg-amber-50 text-amber-700'}`}>
              {approved ? 'Approved' : 'Draft'} · v{cur.version_no}
              {approved && data.requirement_status === 'pending_approval' ? ' — waiting for HR review' : ''}
            </span>
            <button onClick={() => setShowHistory(true)} className="px-4 py-2 rounded-xl border border-gray-300 text-sm font-semibold">Version history</button>
            {approved && !changing ? (
              <button onClick={() => setChanging(true)} className="px-4 py-2 rounded-xl border border-brand-blue text-brand-blue text-sm font-bold">Make changes</button>
            ) : (
              <button onClick={approve} disabled={!!busy || approved || data.blockers.length > 0} data-testid="approve"
                className="px-4 py-2 rounded-xl bg-brand-green text-white text-sm font-bold disabled:opacity-40">
                {busy === 'approve' ? 'Approving…' : '✓ Approve & send to recruiting'}
              </button>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8 space-y-4">
        {cur.human_review && <p role="alert" className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3">{cur.human_review}</p>}
        {data.hr_review_required && (
          <p className="text-sm text-blue-800 bg-blue-50 border border-blue-200 rounded-xl px-4 py-3">
            Work authorization requires US citizenship (option c): after you approve, HR reviews it before the job goes to recruiting.
          </p>
        )}
        {!approved && data.blockers.length > 0 && (
          <ul className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-xl px-4 py-3 list-disc list-inside" data-testid="blockers">
            {data.blockers.map((b) => <li key={b}>{b}</li>)}
          </ul>
        )}
        {approved && changing && <p className="text-sm text-gray-600">Your next saved change starts version {cur.version_no + 1} — approve it again to send it to recruiting.</p>}
        {error && <p className="text-sm text-red-600">{error}</p>}

        <div>
          {/* Tabs — the Requirement Results tab bar */}
          <div className="bg-white rounded-t-lg shadow-sm border border-gray-200 border-b-0">
            <div className="flex border-b border-gray-200" role="tablist">
              {[['jd', '📄', 'Job Description'], ['questions', '❓', `Screening Questions (${questions.length})`]].map(([k, icon, label], index) => (
                <button key={k} role="tab" aria-selected={tab === k} onClick={() => setTab(k)}
                  className={`flex-1 px-6 py-4 font-semibold text-sm transition-colors relative ${
                    tab === k ? 'text-brand-blue bg-blue-50 border-b-2 border-brand-blue' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                  } ${index !== 0 ? 'border-l border-gray-200' : ''}`}>
                  <span className="mr-2 text-lg">{icon}</span>{label}
                </button>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-b-lg shadow-sm border border-gray-200 border-t-0">
            {tab === 'jd' && (
              <div className="p-8" data-testid="jd-tab">
                <div className="flex flex-wrap items-center justify-between gap-3 mb-8">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-blue-600 to-indigo-600 flex items-center justify-center shadow-lg"><span className="text-2xl">📄</span></div>
                    <div>
                      <h2 className="text-2xl font-bold text-gray-900" data-testid="jd-title">{displayTitle(data.req_id, jd.title || data.job_title)}</h2>
                      <p className="text-sm text-gray-500 mt-0.5">Job description — {sections.length} sections</p>
                    </div>
                  </div>
                  <div className="flex gap-3">
                    <button onClick={() => setCollapsed({})} className={plainBtn}>Expand All</button>
                    <button onClick={() => setCollapsed(Object.fromEntries(sections.map((s) => [s.key, true])))} className={plainBtn}>Collapse All</button>
                    <DownloadButton onDownload={download} busy={busy === 'download'} />
                  </div>
                </div>
                <div className="space-y-3">
                  {sections.map((s) => {
                    const style = SECTION_STYLE[s.key] || DEFAULT_STYLE;
                    const isEditing = editing === s.key;
                    const isExpanded = isEditing || !collapsed[s.key];
                    return (
                      <div key={s.key} className="rounded-2xl border border-gray-200 overflow-hidden shadow-sm hover:shadow-md transition-shadow" data-testid={`section-${s.key}`}>
                        <div className={`h-1 ${style.bar}`} />
                        <div className="flex items-center justify-between px-6 py-4 bg-white">
                          <button onClick={() => setCollapsed((c) => ({ ...c, [s.key]: isExpanded }))} aria-expanded={isExpanded}
                            className="flex items-center gap-3 text-left flex-1">
                            <div className={`w-9 h-9 rounded-xl ${style.bar} flex items-center justify-center text-white text-lg shadow-sm`}>{style.icon}</div>
                            <span className="text-base font-bold text-gray-900">{s.label}</span>
                            <span className={`hidden sm:inline-flex px-2.5 py-0.5 rounded-full text-xs font-bold border ${style.badge}`}>
                              {s.kind === 'locked' ? '🔒 From your form' : isExpanded ? 'Expanded' : 'Collapsed'}
                            </span>
                          </button>
                          <div className="flex items-center gap-2">
                            {s.editable && canEdit && !isEditing && (
                              <button onClick={() => startEdit(s)} title={`Edit ${s.label}`} aria-label={`Edit ${s.label}`}
                                className="w-8 h-8 rounded-lg flex items-center justify-center text-brand-blue hover:bg-blue-50">
                                <PencilSquareIcon className="w-5 h-5" />
                              </button>
                            )}
                            <div className={`w-7 h-7 rounded-full flex items-center justify-center transition-transform ${isExpanded ? 'bg-gray-200 rotate-180' : 'bg-gray-100'}`}>
                              <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                              </svg>
                            </div>
                          </div>
                        </div>
                        {isExpanded && (
                          <div className="border-t border-gray-100">
                            <div className={`border-l-4 ${style.accent} mx-6 my-5 pl-4 ${style.bg} rounded-r-xl py-4 pr-4`}>
                              {isEditing ? (
                                <div>
                                  {s.kind === 'groups' ? <GroupsEditor groups={draft} onChange={setDraft} /> : (
                                    <>
                                      <textarea aria-label={s.label} rows={s.kind === 'paragraphs' ? 6 : 5} value={draft} onChange={(e) => setDraft(e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white" />
                                      <p className="text-[11px] text-gray-500 mt-0.5">
                                        {{ tags: 'One per line — "Label: value".', table: 'One row per line — "Category: skills".',
                                          paragraphs: 'Leave a blank line between paragraphs.' }[s.kind] || 'One bullet per line.'}
                                      </p>
                                    </>
                                  )}
                                  <div className="flex justify-end gap-2 mt-2">
                                    <button onClick={() => setEditing(null)} className="px-4 py-2 rounded-lg bg-gray-100 text-gray-700 text-sm font-semibold">Cancel</button>
                                    <button onClick={() => saveSection(s)} disabled={!!busy} data-testid="section-save"
                                      className="px-4 py-2 rounded-lg bg-green-600 text-white text-sm font-bold disabled:opacity-50">Save</button>
                                  </div>
                                </div>
                              ) : <SectionView section={s} value={jd[s.key]} />}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {tab === 'questions' && (
              <div className="p-8" data-testid="questions-tab">
                <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
                  <div className="flex items-center gap-3">
                    <span className="text-3xl">❓</span>
                    <div>
                      <h2 className="text-2xl font-bold text-gray-900">Screening Questions</h2>
                      <p className="text-sm text-gray-500 mt-0.5" data-testid="questions-subtitle">
                        {questions.length} technical knowledge checks for level 1 candidate screening
                      </p>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-3">
                    {canEdit && data.can_regenerate_questions && (
                      <button onClick={() => setConfirmRegen(true)} disabled={!!busy} className={plainBtn}>↻ Regenerate questions</button>
                    )}
                    {canEdit && qEditing === null && (
                      <button onClick={() => setQEditing('new')} className="px-4 py-2 rounded-lg bg-brand-blue text-white text-sm font-bold">+ Add question</button>
                    )}
                    <DownloadButton onDownload={download} busy={busy === 'download'} />
                  </div>
                </div>
                {confirmRegen && (
                  <div className="mb-6 flex flex-wrap items-center justify-between gap-3 bg-blue-50 border border-blue-200 rounded-xl px-4 py-3 text-sm text-blue-900" data-testid="confirm-regenerate">
                    {busy === 'regenerate'
                      ? <span className="inline-flex items-center gap-2"><span className="w-4 h-4 rounded-full border-2 border-blue-200 border-t-brand-blue animate-spin" />Writing new questions… this takes about a minute.</span>
                      : <span>Replace all questions with new ones? Your current questions stay in version {cur.version_no}.</span>}
                    {busy !== 'regenerate' && (
                      <span className="flex gap-2">
                        <button onClick={() => setConfirmRegen(false)} className="px-3 py-1 rounded bg-white border border-gray-200 text-gray-700">Keep these</button>
                        <button onClick={regenerate} className="px-3 py-1 rounded bg-brand-blue text-white font-bold">Regenerate</button>
                      </span>
                    )}
                  </div>
                )}
                <div className="mb-6"><SkillChips questions={questions} /></div>
                <div className="space-y-5">
                  {questions.map((q, i) => (
                    <ScreeningQuestionCard key={`${i}-${q.question}`} q={q} index={i} total={questions.length} busy={!!busy}
                      editing={qEditing === i}
                      {...(canEdit ? { onEdit: () => setQEditing(i), onSave: saveQuestion, onCancel: () => setQEditing(null),
                        onDelete: () => removeQuestion(i), onMove: (d) => move(i, d) } : {})} />
                  ))}
                  {qEditing === 'new' && (
                    <div data-testid="question-new">
                      <ScreeningQuestionCard q={EMPTY_Q} index={questions.length} total={questions.length + 1} busy={!!busy} editing
                        onSave={saveQuestion} onCancel={() => setQEditing(null)} onDelete={() => true} onMove={() => {}} />
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </main>

      {showHistory && (
        <div className="fixed inset-0 bg-black/30 flex items-center justify-center p-4 z-50" role="dialog" aria-label="Version history">
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6 w-full max-w-2xl max-h-[80vh] overflow-y-auto">
            <div className="flex justify-between mb-3">
              <h2 className="text-lg font-black text-gray-900">Version history</h2>
              <button onClick={() => { setShowHistory(false); setViewing(null); }} aria-label="Close" className="text-gray-400">✕</button>
            </div>
            <ul className="text-sm space-y-1">
              {data.versions.map((v) => (
                <li key={v.id}>
                  <button className="text-brand-blue hover:underline" onClick={() => intakeApi(`/${intakeId}/versions/${v.version_no}`).then(setViewing).catch((e) => setError(e.message))}>
                    Version {v.version_no}
                  </button>
                  <span className="text-gray-500"> · {v.status}{v.approved_at ? ` ${new Date(v.approved_at).toLocaleDateString()}` : ''}</span>
                </li>
              ))}
            </ul>
            {viewing && (
              <div className="mt-3 border-t border-gray-100 pt-3 text-sm text-gray-700" data-testid="version-view">
                <p className="font-bold">Version {viewing.version_no} ({viewing.status})</p>
                {((viewing.jd || {}).summary || []).map((p) => <p key={p} className="mt-1">{p}</p>)}
                <ol className="list-decimal list-inside mt-2">{(viewing.screening_questions || []).map((q) => <li key={q.question}>{q.question}</li>)}</ol>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default JobReviewPage;
